import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { Alert, Button, Form, Modal } from 'react-bootstrap';

import DateRangePicker from 'components/Common/DateRangePicker/DateRangePicker';
import Currency from 'components/Common/Currency/Currency';
import {
  commitBulkWriteback,
  getTransactionCountByDateRange,
  prepareBulkWriteback,
  validateBulkWriteback,
} from 'utils/api/transactionApi';

const Steps = Object.freeze({
  RANGE: 'range',
  PREVIOUS_ATTEMPTS: 'previousAttempts',
  RESOLVE: 'resolve',
  REVIEW: 'review',
  SUBMITTED: 'submitted',
});

function getErrorMessage(error, fallback) {
  return error?.userMessage || error?.message || fallback;
}

function BulkWritebackModal({
  show,
  onClose,
  practiceId,
  onWritebackAccepted,
}) {
  const [step, setStep] = useState(Steps.RANGE);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [transactionCount, setTransactionCount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [excludedCount, setExcludedCount] = useState(0);
  const [initialInvalidCount, setInitialInvalidCount] = useState(0);
  const [corrections, setCorrections] = useState({});
  const [forcedTransactionIds, setForcedTransactionIds] = useState([]);
  const [repeatTransactionIds, setRepeatTransactionIds] = useState([]);
  const [correctionInput, setCorrectionInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [operation, setOperation] = useState(null);
  const [batchId, setBatchId] = useState(uuid());

  const unresolvedTransactions = useMemo(
    () =>
      transactions.filter(
        (transaction) =>
          !transaction.valid &&
          !forcedTransactionIds.includes(transaction.payHeroTransactionId)
      ),
    [forcedTransactionIds, transactions]
  );
  const currentInvalidTransaction = unresolvedTransactions[0];
  const previouslyAttemptedTransactions = useMemo(
    () => transactions.filter((transaction) => transaction.writebackBatchId),
    [transactions]
  );

  useEffect(() => {
    setCorrectionInput(currentInvalidTransaction?.pmsInvoiceId ?? '');
  }, [currentInvalidTransaction]);

  const getDates = () => ({
    start: startDate.clone().startOf('day').valueOf(),
    final: endDate.clone().endOf('day').valueOf(),
  });

  const getPracticeBody = () => (practiceId ? { practiceId } : {});

  const reset = () => {
    setStep(Steps.RANGE);
    setStartDate(null);
    setEndDate(null);
    setTransactionCount(null);
    setTransactions([]);
    setExcludedCount(0);
    setInitialInvalidCount(0);
    setCorrections({});
    setForcedTransactionIds([]);
    setRepeatTransactionIds([]);
    setCorrectionInput('');
    setIsLoading(false);
    setError('');
    setOperation(null);
    setBatchId(uuid());
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDatesChange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setTransactionCount(null);
    setError('');
  };

  const handleFindTransactions = async () => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    setError('');

    try {
      const count = await getTransactionCountByDateRange(
        getDates().start,
        getDates().final,
        practiceId
      );
      setTransactionCount(count);
    } catch (err) {
      setError(
        getErrorMessage(err, 'Could not load transactions for this date range.')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrepareWriteback = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await prepareBulkWriteback({
        ...getPracticeBody(),
        dates: getDates(),
      });
      if (!response.total) {
        setError('No eligible transactions were found in this date range.');
        return;
      }

      setTransactions(response.transactions);
      setExcludedCount(response.excludedCount || 0);
      setInitialInvalidCount(response.invalidCount || 0);
      setStep(
        response.transactions.some(
          (transaction) => transaction.writebackBatchId
        )
          ? Steps.PREVIOUS_ATTEMPTS
          : response.invalidCount > 0
          ? Steps.RESOLVE
          : Steps.REVIEW
      );
    } catch (err) {
      setError(
        getErrorMessage(err, 'Could not prepare transactions for writeback.')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const continueAfterPreviousAttempts = (nextTransactions) => {
    const invalidCount = nextTransactions.filter(
      (transaction) => !transaction.valid
    ).length;
    setTransactions(nextTransactions);
    setInitialInvalidCount(invalidCount);
    setStep(invalidCount > 0 ? Steps.RESOLVE : Steps.REVIEW);
  };

  const handleExcludePreviousAttempts = () => {
    const remaining = transactions.filter(
      (transaction) => !transaction.writebackBatchId
    );
    if (remaining.length === 0) {
      setError(
        'Every eligible transaction in this range has already had a writeback attempt. Include them again or choose another range.'
      );
      return;
    }

    setError('');
    setRepeatTransactionIds([]);
    setExcludedCount(
      (current) => current + previouslyAttemptedTransactions.length
    );
    continueAfterPreviousAttempts(remaining);
  };

  const handleIncludePreviousAttempts = () => {
    setError('');
    setRepeatTransactionIds(
      previouslyAttemptedTransactions.map(
        (transaction) => transaction.payHeroTransactionId
      )
    );
    continueAfterPreviousAttempts(transactions);
  };

  const updateCurrentTransaction = (validation, forced = false) => {
    const transactionId = currentInvalidTransaction.payHeroTransactionId;
    const pmsInvoiceId = correctionInput.trim();
    const updatedTransactions = transactions.map((transaction) =>
      transaction.payHeroTransactionId === transactionId
        ? {
            ...transaction,
            pmsInvoiceId,
            valid: validation.valid,
            validationMessage: validation.validationMessage || '',
          }
        : transaction
    );
    const updatedForcedIds = forced
      ? [...new Set([...forcedTransactionIds, transactionId])]
      : forcedTransactionIds;

    setTransactions(updatedTransactions);
    setForcedTransactionIds(updatedForcedIds);
    if (pmsInvoiceId !== currentInvalidTransaction.pmsInvoiceId) {
      setCorrections((current) => ({
        ...current,
        [transactionId]: pmsInvoiceId,
      }));
    }

    const remaining = updatedTransactions.filter(
      (transaction) =>
        !transaction.valid &&
        !updatedForcedIds.includes(transaction.payHeroTransactionId)
    );
    if (remaining.length === 0) setStep(Steps.REVIEW);
  };

  const handleRecheck = async () => {
    if (!correctionInput.trim()) {
      setError('Enter a PMS invoice ID or choose Send Anyway.');
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      const response = await validateBulkWriteback({
        ...getPracticeBody(),
        items: [
          {
            payHeroTransactionId:
              currentInvalidTransaction.payHeroTransactionId,
            pmsInvoiceId: correctionInput,
          },
        ],
      });
      const validation = response.items[0];
      if (!validation.valid) {
        setError(
          validation.validationMessage || 'Invoice ID is still invalid.'
        );
        return;
      }
      updateCurrentTransaction(validation);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not validate this invoice ID.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendAnyway = () => {
    if (!correctionInput.trim()) {
      setError('Enter a PMS invoice ID before choosing Send Anyway.');
      return;
    }
    setError('');
    updateCurrentTransaction(
      {
        valid: false,
        validationMessage: currentInvalidTransaction.validationMessage,
      },
      true
    );
  };

  const handleCommit = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await commitBulkWriteback({
        ...getPracticeBody(),
        dates: getDates(),
        payHeroTransactionIds: transactions.map(
          (transaction) => transaction.payHeroTransactionId
        ),
        corrections: Object.entries(corrections).map(
          ([payHeroTransactionId, pmsInvoiceId]) => ({
            payHeroTransactionId,
            pmsInvoiceId,
          })
        ),
        forcedTransactionIds,
        repeatTransactionIds,
        batchId,
      });
      setOperation(response);
      setStep(Steps.SUBMITTED);
      onWritebackAccepted();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not submit the writeback command.'));
    } finally {
      setIsLoading(false);
    }
  };

  const isOutsideRange = ({ day, focusedInput }) => {
    if (day.isAfter(moment(), 'day')) return true;
    return focusedInput === 'endDate' && startDate
      ? day.isBefore(startDate, 'day')
      : false;
  };

  const renderRange = () => (
    <>
      <p>Select the transaction date range to prepare for writeback.</p>
      <DateRangePicker
        label='Date Range'
        startDate={startDate}
        startDateId='bulkWritebackStartDate'
        endDate={endDate}
        endDateId='bulkWritebackEndDate'
        onDatesChange={handleDatesChange}
        minimumNights={0}
        hideKeyboardShortcutsPanel={true}
        isOutsideRange={isOutsideRange}
        disabled={isLoading}
      />
      {transactionCount !== null && (
        <Alert className='mt-3 mb-0' variant='info'>
          {transactionCount} transaction{transactionCount === 1 ? '' : 's'}{' '}
          found.
        </Alert>
      )}
    </>
  );

  const renderResolve = () => {
    const resolvedCount = initialInvalidCount - unresolvedTransactions.length;

    return (
      <>
        <p>
          Resolve invalid invoice ID {resolvedCount + 1} of{' '}
          {initialInvalidCount}.
        </p>
        <dl className='row mb-3'>
          <dt className='col-5'>Transaction</dt>
          <dd className='col-7'>
            {currentInvalidTransaction.processorTransactionId}
          </dd>
          <dt className='col-5'>Date</dt>
          <dd className='col-7'>
            {moment(currentInvalidTransaction.transactionDate).format('L LT')}
          </dd>
          <dt className='col-5'>Amount</dt>
          <dd className='col-7'>
            <Currency
              amount={currentInvalidTransaction.amount}
              defaultToZero={true}
            />
          </dd>
        </dl>
        <Form.Group controlId='correctedPmsInvoiceId'>
          <Form.Label>PMS Invoice ID</Form.Label>
          <Form.Control
            value={correctionInput}
            onChange={(event) => setCorrectionInput(event.target.value)}
            disabled={isLoading}
          />
          <Form.Text className='text-danger'>
            {currentInvalidTransaction.validationMessage}
          </Form.Text>
        </Form.Group>
      </>
    );
  };

  const renderPreviousAttempts = () => (
    <>
      <Alert variant='warning'>
        {previouslyAttemptedTransactions.length} transaction
        {previouslyAttemptedTransactions.length === 1 ? ' has' : 's have'}{' '}
        already had a writeback attempt. Review them before including them in
        another batch.
      </Alert>
      <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
        {previouslyAttemptedTransactions.map((transaction) => (
          <div
            className='border-bottom pb-2 mb-2'
            key={transaction.payHeroTransactionId}
          >
            <div>
              <strong>{transaction.processorTransactionId}</strong>
              {' · '}
              <Currency amount={transaction.amount} defaultToZero={true} />
            </div>
            <small className='text-muted'>
              PMS ID: {transaction.pmsInvoiceId}
              {' · '}Status: {transaction.writebackStatus || 'attempted'}
              {transaction.writebackAcceptedAt && (
                <>
                  {' · '}
                  Accepted:{' '}
                  {moment(transaction.writebackAcceptedAt).format('L LT')}
                </>
              )}
            </small>
          </div>
        ))}
      </div>
      <p className='mb-0 mt-3'>
        You can exclude these transactions or explicitly include them again.
      </p>
    </>
  );

  const renderReview = () => (
    <>
      <h5>Review writeback</h5>
      <dl className='row mb-0'>
        <dt className='col-8'>Transactions to submit</dt>
        <dd className='col-4'>{transactions.length}</dd>
        <dt className='col-8'>Corrected invoice IDs</dt>
        <dd className='col-4'>{Object.keys(corrections).length}</dd>
        <dt className='col-8'>Sending despite invalid ID</dt>
        <dd className='col-4'>{forcedTransactionIds.length}</dd>
        <dt className='col-8'>Excluded transactions</dt>
        <dd className='col-4'>{excludedCount}</dd>
        <dt className='col-8'>Previously attempted, included again</dt>
        <dd className='col-4'>{repeatTransactionIds.length}</dd>
      </dl>
      {forcedTransactionIds.length > 0 && (
        <Alert className='mt-3 mb-0' variant='warning'>
          VetHero Core will receive {forcedTransactionIds.length} explicitly
          forced transaction{forcedTransactionIds.length === 1 ? '' : 's'}.
        </Alert>
      )}
      {repeatTransactionIds.length > 0 && (
        <Alert className='mt-3 mb-0' variant='warning'>
          You explicitly chose to submit {repeatTransactionIds.length}{' '}
          previously attempted transaction
          {repeatTransactionIds.length === 1 ? '' : 's'} again.
        </Alert>
      )}
    </>
  );

  const renderSubmitted = () => {
    const operationDetails = operation?.operation || {};
    const operationId = operationDetails.operationId;
    return (
      <Alert className='mb-0' variant='success'>
        Writeback accepted for {operation?.submittedCount} transaction
        {operation?.submittedCount === 1 ? '' : 's'}.
        {operationId && <div>Operation ID: {operationId}</div>}
      </Alert>
    );
  };

  const renderFooter = () => {
    if (step === Steps.SUBMITTED) {
      return <Button onClick={handleClose}>Close</Button>;
    }

    return (
      <>
        <Button variant='light' onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        {step === Steps.RANGE && transactionCount === null && (
          <Button
            onClick={handleFindTransactions}
            disabled={isLoading || !startDate || !endDate}
          >
            {isLoading ? 'Finding...' : 'Find Transactions'}
          </Button>
        )}
        {step === Steps.RANGE && transactionCount !== null && (
          <Button
            onClick={handlePrepareWriteback}
            disabled={isLoading || transactionCount === 0}
          >
            {isLoading ? 'Validating...' : 'Writeback'}
          </Button>
        )}
        {step === Steps.RESOLVE && (
          <>
            <Button
              variant='outline-secondary'
              onClick={handleSendAnyway}
              disabled={isLoading}
            >
              Send Anyway
            </Button>
            <Button onClick={handleRecheck} disabled={isLoading}>
              {isLoading ? 'Checking...' : 'Recheck'}
            </Button>
          </>
        )}
        {step === Steps.PREVIOUS_ATTEMPTS && (
          <>
            <Button
              variant='outline-secondary'
              onClick={handleExcludePreviousAttempts}
              disabled={isLoading}
            >
              Exclude Previous Attempts
            </Button>
            <Button
              onClick={handleIncludePreviousAttempts}
              disabled={isLoading}
            >
              Include Again
            </Button>
          </>
        )}
        {step === Steps.REVIEW && (
          <Button onClick={handleCommit} disabled={isLoading}>
            {isLoading ? 'Submitting...' : 'Confirm Writeback'}
          </Button>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Bulk Writeback</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === Steps.RANGE && renderRange()}
        {step === Steps.PREVIOUS_ATTEMPTS && renderPreviousAttempts()}
        {step === Steps.RESOLVE && currentInvalidTransaction && renderResolve()}
        {step === Steps.REVIEW && renderReview()}
        {step === Steps.SUBMITTED && renderSubmitted()}
        {error && (
          <Alert className='mt-3 mb-0' variant='danger'>
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>{renderFooter()}</Modal.Footer>
    </Modal>
  );
}

BulkWritebackModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  practiceId: PropTypes.string,
  onWritebackAccepted: PropTypes.func,
};

BulkWritebackModal.defaultProps = {
  onWritebackAccepted: () => {},
};

export default BulkWritebackModal;
